// Tai khoan cua toi + danh sach nguoi dung khac (ai da lam gi).

import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { Alert, Badge, ErrorBox, Spinner } from "../components/ui";
import KhungVNC from "../components/KhungVNC";
import type { NguoiDung, PhienPanel, RunMeta } from "../types";

function LamMoiPhien({ taiKhoan, onXong }: { taiKhoan: string; onXong: () => void }) {
  const [matKhau, setMatKhau] = useState("");
  const [phien, setPhien] = useState<PhienPanel | null>(null);
  const [loi, setLoi] = useState<unknown>(null);
  const [dangGui, setDangGui] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!phien || phien.xong) return;
    const t = window.setInterval(async () => {
      try {
        setPhien((await api.xemPhienPanel(phien.id)).phien);
      } catch {
        window.clearInterval(t);
      }
    }, 1500);
    return () => window.clearInterval(t);
  }, [phien?.id, phien?.xong]);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [phien?.log]);

  const mo = async () => {
    setLoi(null);
    setDangGui(true);
    try {
      setPhien((await api.moPhienPanel(taiKhoan, matKhau)).phien);
      setMatKhau("");
    } catch (e) {
      setLoi(e);
    } finally {
      setDangGui(false);
    }
  };

  if (phien) {
    return (
      <div>
        {phien.trang_thai === "xong" ? (
          <Alert tone="ok">{phien.thong_diep}</Alert>
        ) : phien.xong ? (
          <Alert tone="danger">{phien.thong_diep}</Alert>
        ) : (
          <Alert tone="info">
            Tick vào ô “Tôi không phải người máy” trong khung bên dưới. Còn {phien.con_lai}s.
          </Alert>
        )}

        {!phien.xong && phien.vnc_url ? (
          <KhungVNC url={phien.vnc_url} sanSang={phien.vnc_san_sang} />
        ) : null}

        <div ref={logRef} className="console-body"
             style={{ marginTop: 12, maxHeight: 130, background: "#171b23",
                      borderRadius: "var(--radius)" }}>
          {phien.log.map((d, i) => (
            <div key={i} className="console-line"><span>{d}</span></div>
          ))}
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <button
            className={phien.trang_thai === "xong" ? "btn btn-primary" : "btn"}
            onClick={async () => {
              if (!phien.xong) await api.huyPhienPanel(phien.id).catch(() => undefined);
              setPhien(null);
              onXong();
            }}
          >
            {phien.trang_thai === "xong" ? "Xong" : phien.xong ? "Đóng" : "Huỷ"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="param-grid">
        <div className="field">
          <label className="label">Nhập lại mật khẩu để mở phiên</label>
          <input
            className="input"
            type="password"
            value={matKhau}
            onChange={(e) => setMatKhau(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && matKhau && mo()}
          />
        </div>
      </div>
      {loi ? <div style={{ marginBottom: 12 }}><ErrorBox error={loi} /></div> : null}
      <button className="btn btn-primary" onClick={mo} disabled={!matKhau || dangGui}>
        {dangGui ? <Spinner /> : null} Mở phiên đăng nhập panel
      </button>
    </div>
  );
}

export default function TaiKhoanPage() {
  const { toi, coPhienPanel, napLai, thoat } = useAuth();
  const [moiNguoi, setMoiNguoi] = useState<NguoiDung[] | null>(null);
  const [runs, setRuns] = useState<RunMeta[]>([]);
  const [loi, setLoi] = useState<unknown>(null);
  const [moLamMoi, setMoLamMoi] = useState(false);

  const nap = () => {
    api.dsNguoiDung().then((r) => setMoiNguoi(r.nguoi_dung)).catch(setLoi);
    api.runs(200).then((r) => setRuns(r.runs)).catch(() => undefined);
  };
  useEffect(nap, []);

  if (!toi) return null;

  // "Da lam duoc nhung du an gi": gom cac lan chay theo bo du lieu da dung.
  const thongKe = {
    tong: runs.length,
    thanh_cong: runs.filter((r) => r.status === "success").length,
    that_bai: runs.filter((r) => r.status === "failed" || r.status === "error").length,
    gan_nhat: runs[0]?.started_at ?? "—",
  };

  return (
    <div className="content-pad">
      <div className="card">
        <div className="card-head">
          <h2 className="grow">Tài khoản của tôi</h2>
          {toi.quan_tri ? <Badge tone="accent">Quản trị</Badge> : null}
        </div>
        <div className="card-body">
          <dl className="kv">
            <dt>Tài khoản SKU</dt>
            <dd><strong>{toi.tai_khoan}</strong></dd>
            <dt>Thiết lập lúc</dt>
            <dd>{toi.tao_luc}</dd>
            <dt>Đăng nhập gần nhất</dt>
            <dd>{toi.dang_nhap_cuoi ?? "—"}</dd>
            <dt>Phiên panel</dt>
            <dd>
              {coPhienPanel ? (
                <Badge tone="ok">Còn hiệu lực · lưu lúc {toi.phien_panel_luc}</Badge>
              ) : (
                <Badge tone="danger">Chưa có — các bước cần Chrome sẽ không chạy được</Badge>
              )}
            </dd>
            <dt>Thư mục dữ liệu riêng</dt>
            <dd className="mono small">backend/data/nd/{toi.slug}/</dd>
          </dl>

          <div style={{ marginTop: 16 }}>
            {moLamMoi ? (
              <LamMoiPhien
                taiKhoan={toi.tai_khoan}
                onXong={() => {
                  setMoLamMoi(false);
                  void napLai();
                  nap();
                }}
              />
            ) : (
              <div className="row">
                <button className="btn btn-primary" onClick={() => setMoLamMoi(true)}>
                  {coPhienPanel ? "Đăng nhập lại panel" : "Đăng nhập panel ngay"}
                </button>
                <button className="btn" onClick={() => void thoat()}>Đăng xuất</button>
              </div>
            )}
          </div>

          {!coPhienPanel && !moLamMoi ? (
            <div style={{ marginTop: 14 }}>
              <Alert tone="warn">
                Phiên panel hết hạn hoặc chưa có. Bước 3 và 6 cần phiên này; hai bước
                còn lại vẫn chạy bình thường.
              </Alert>
            </div>
          ) : null}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><h2 className="grow">Công việc của tôi</h2></div>
        <div className="card-body">
          <div className="stat-grid">
            <div className="stat">
              <div className="sv">{thongKe.tong}</div>
              <div className="sl">lượt chạy</div>
            </div>
            <div className="stat">
              <div className="sv" style={{ color: "var(--ok)" }}>{thongKe.thanh_cong}</div>
              <div className="sl">thành công</div>
            </div>
            <div className="stat">
              <div className="sv" style={{ color: "var(--danger)" }}>{thongKe.that_bai}</div>
              <div className="sl">thất bại</div>
            </div>
            <div className="stat">
              <div className="sv" style={{ fontSize: 15 }}>{thongKe.gan_nhat}</div>
              <div className="sl">gần nhất</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head">
          <h2 className="grow">Người dùng trên hệ thống</h2>
          <button className="btn btn-sm" onClick={nap}>↻ Nạp lại</button>
        </div>
        {loi ? <div className="card-body"><ErrorBox error={loi} /></div> : null}
        {moiNguoi === null ? (
          <div className="card-body"><Spinner /></div>
        ) : (
          <div className="bang-cuon">
          <table className="table">
            <thead>
              <tr>
                <th>Tài khoản</th>
                <th>Thiết lập</th>
                <th>Đăng nhập gần nhất</th>
                <th>Phiên panel</th>
              </tr>
            </thead>
            <tbody>
              {moiNguoi.map((n) => (
                <tr key={n.slug}>
                  <td>
                    <strong>{n.tai_khoan}</strong>
                    {n.slug === toi.slug ? <> <Badge tone="accent">bạn</Badge></> : null}
                    {n.quan_tri ? <> <Badge>quản trị</Badge></> : null}
                  </td>
                  <td className="small">{n.tao_luc}</td>
                  <td className="small">{n.dang_nhap_cuoi ?? "—"}</td>
                  <td>
                    {n.co_phien_panel ? (
                      <Badge tone="ok">còn</Badge>
                    ) : (
                      <Badge tone="warn">hết</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        <div className="card-body" style={{ paddingTop: 0 }}>
          <div className="hint">
            Mỗi người chỉ xem được dữ liệu và lịch sử chạy của chính mình. Bảng này
            chỉ liệt kê ai đã thiết lập tài khoản, không lộ dữ liệu của họ.
          </div>
        </div>
      </div>
    </div>
  );
}
